import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

import AddAdminModal from "./AddAdminModal";
import Avatar from "./component/Avatar";

function AdminTable({ onRowClick }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const PER = 15;

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("adminToken");

      const response = await fetch(
        "https://testone.conversationaily.ai/api/admins",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      //   const adminList = (data?.admins || []).filter(
      //     (item) =>
      //       item.role?.toLowerCase() === "admin" ||
      //       item.user_type?.toLowerCase() === "admin"
      //   );

      setAdmins(data?.admins || []);
    } catch (error) {
      console.error("Failed to fetch admins:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateAdminStatus = async (admin) => {
    try {
      const token = localStorage.getItem("adminToken");

      const newStatus =
        admin.account_status === "Active"
          ? "Inactive"
          : "Active";

      const response = await fetch(
        `https://testone.conversationaily.ai/api/admin/users/${admin.id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            account_status: newStatus,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update status");
      }

      // Update local state without refetching
      fetchAdmins()
      // setAdmins((prev) =>
      //   prev.map((item) =>
      //     item.id === admin.id
      //       ? { ...item, account_status: newStatus }
      //       : item
      //   )
      // );
    } catch (error) {
      console.error("Status update failed:", error);
    }
  };

  const deleteAdmin = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this admin?"
    );

    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("adminToken");

      const response = await fetch(
        `https://consult.conversationaily.ai/api/admin/${id}/delete`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Delete failed");
      }

      // Update local state
      // setAdmins((prev) => prev.filter((item) => item.id !== id));

      fetchAdmins();

    } catch (err) {
      console.error(err);
      alert("Failed to delete admin");
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    return admins.filter(
      (admin) =>
        !q ||
        `${admin.first_name || ""} ${admin.last_name || ""}`
          .toLowerCase()
          .includes(q) ||
        admin.email?.toLowerCase().includes(q)
    );
  }, [admins, search]);

  const pages = Math.ceil(filtered.length / PER);
  const visible = filtered.slice(page * PER, (page + 1) * PER);


  return (
    <>
      <div className="space-y-4">
        {/* Search + Add Admin */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"
            />

            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search admin by name or email..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          <button
            onClick={() => setShowAddAdminModal(true)}
            className="px-4 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 transition"
          >
            + Add Admin
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                {[
                  "Name",
                  "Email",
                  "Role",
                  "Organization",
                  "Created",
                  "Action"
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </thead>

              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center py-10 text-gray-500"
                    >
                      Loading admins...
                    </td>
                  </tr>
                ) : visible.length ? (
                  visible.map((admin) => (
                    <tr
                      key={admin.id}
                      onClick={() => onRowClick?.(admin)}
                      className="hover:bg-orange-50/50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar
                            firstName={admin.first_name}
                            lastName={admin.last_name}
                          />

                          <span className="font-semibold text-gray-800 whitespace-nowrap">
                            {admin.first_name} {admin.last_name}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {admin.email || "—"}
                      </td>

                      <td className="px-4 py-3">
                        <span className="bg-blue-100 text-blue-700 border border-blue-200 text-xs font-semibold px-2 py-0.5 rounded-full">
                          {admin.role || admin.user_type || "Admin"}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {admin.organization || "—"}
                      </td>

                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {admin.created_at
                          ? new Date(
                            admin.created_at
                          ).toLocaleDateString()
                          : "—"}
                      </td>


                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-2">


                          <button
                            onClick={() => updateAdminStatus(admin)}
                            className={`min-w-[90px] px-3 py-1.5 rounded-md text-xs font-medium transition-colors
      ${admin.account_status === "Active"
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-red-100 text-red-700 hover:bg-red-200"
                              }`}
                          >
                            {admin.account_status === "Active" ? "Active" : "Inactive"}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedAdmin(admin);
                              setShowAddAdminModal(true);
                            }}
                            className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                            title="Edit"
                          >
                            <FiEdit2 size={16} />
                          </button>

                          <button
                            onClick={() => deleteAdmin(admin.id)}
                            className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition"
                            title="Delete"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-10 text-gray-400"
                    >
                      No admins found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
            <span>
              {filtered.length
                ? `${page * PER + 1}-${Math.min(
                  (page + 1) * PER,
                  filtered.length
                )} of ${filtered.length}`
                : "0 admins"}
            </span>

            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPage((prev) => Math.max(prev - 1, 0))
                }
                disabled={page === 0}
                className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30"
              >
                ← Prev
              </button>

              <span className="px-2 py-1.5">
                {pages ? page + 1 : 0}/{pages || 0}
              </span>

              <button
                onClick={() =>
                  setPage((prev) =>
                    Math.min(prev + 1, pages - 1)
                  )
                }
                disabled={page >= pages - 1 || pages === 0}
                className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>



      <AddAdminModal
        isOpen={showAddAdminModal}
        admin={selectedAdmin}
        onClose={() => {
          setShowAddAdminModal(false);
          setSelectedAdmin(null);
        }}
        onSuccess={() => {
          fetchAdmins();
          setSelectedAdmin(null);
        }}
      />
    </>
  );
}

export default AdminTable;